import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '@/lib/utils';
import {
  CalendarDays,
  ChevronRight,
  X,
  CalendarRange,
} from 'lucide-react';

// Indian Fiscal Year: Apr-Mar
const FY_QUARTERS = [
  { label: 'Q1', value: 1, months: [3, 4, 5], display: 'Apr - Jun' },
  { label: 'Q2', value: 2, months: [6, 7, 8], display: 'Jul - Sep' },
  { label: 'Q3', value: 3, months: [9, 10, 11], display: 'Oct - Dec' },
  { label: 'Q4', value: 4, months: [0, 1, 2], display: 'Jan - Mar' },
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function getFiscalYearRange() {
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-11
  const currentCalYear = now.getFullYear();
  // Fiscal year starts in April. If we're in Jan-Mar, current FY started last calendar year.
  const currentFYStart = currentMonth >= 3 ? currentCalYear : currentCalYear - 1;
  const years = [];
  for (let fy = currentFYStart; fy >= currentFYStart - 5; fy--) {
    years.push(fy); // fy represents start year, e.g. 2025 means FY 2025-26
  }
  return years;
}

function fyLabel(fy) {
  return `FY ${fy}-${String(fy + 1).slice(2)}`;
}

function formatDateStr(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDisplay(d) {
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function DateHierarchyFilter({ onDateChange }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('hierarchy'); // 'hierarchy' | 'custom'

  // Hierarchy state
  const [selectedFY, setSelectedFY] = useState(null); // start year of FY
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null); // 0-11
  const [selectedDay, setSelectedDay] = useState(null);

  // Custom range state
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  const [pickingWhich, setPickingWhich] = useState('from');

  // Active label for the trigger button
  const activeLabel = useMemo(() => {
    if (mode === 'custom' && customFrom && customTo) {
      return `${formatDisplay(customFrom)} – ${formatDisplay(customTo)}`;
    }
    if (mode === 'custom' && customFrom) {
      return `From ${formatDisplay(customFrom)}`;
    }
    if (selectedDay) {
      return formatDisplay(selectedDay);
    }
    if (selectedMonth !== null && selectedFY !== null) {
      return `${MONTHS[selectedMonth]} ${selectedMonth >= 3 ? selectedFY : selectedFY + 1}`;
    }
    if (selectedQuarter && selectedFY !== null) {
      const q = FY_QUARTERS.find(q => q.value === selectedQuarter);
      return `${q.label} ${fyLabel(selectedFY)} (${q.display})`;
    }
    if (selectedFY !== null) {
      return fyLabel(selectedFY);
    }
    return null;
  }, [mode, selectedFY, selectedQuarter, selectedMonth, selectedDay, customFrom, customTo]);

  const applyHierarchyFilter = (fy, quarter, month, day) => {
    if (day) {
      const ds = formatDateStr(day);
      onDateChange(ds, ds);
    } else if (month !== null && fy !== null) {
      // month 0-11. Calendar year depends on FY: Apr-Dec = fy, Jan-Mar = fy+1
      const calYear = month >= 3 ? fy : fy + 1;
      const start = new Date(calYear, month, 1);
      const end = new Date(calYear, month + 1, 0);
      onDateChange(formatDateStr(start), formatDateStr(end));
    } else if (quarter && fy !== null) {
      const q = FY_QUARTERS.find(q => q.value === quarter);
      const startMonth = q.months[0];
      const endMonth = q.months[2];
      const startYear = startMonth >= 3 ? fy : fy + 1;
      const endYear = endMonth >= 3 ? fy : fy + 1;
      const start = new Date(startYear, startMonth, 1);
      const end = new Date(endYear, endMonth + 1, 0);
      onDateChange(formatDateStr(start), formatDateStr(end));
    } else if (fy !== null) {
      // Full FY: April fy to March fy+1
      onDateChange(`${fy}-04-01`, `${fy + 1}-03-31`);
    } else {
      onDateChange(null, null);
    }
  };

  const handleFYSelect = (fy) => {
    setSelectedFY(fy);
    setSelectedQuarter(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    applyHierarchyFilter(fy, null, null, null);
  };

  const handleQuarterSelect = (quarter) => {
    setSelectedQuarter(quarter);
    setSelectedMonth(null);
    setSelectedDay(null);
    applyHierarchyFilter(selectedFY, quarter, null, null);
  };

  const handleMonthSelect = (monthIdx) => {
    setSelectedMonth(monthIdx);
    setSelectedDay(null);
    applyHierarchyFilter(selectedFY, selectedQuarter, monthIdx, null);
  };

  const handleDaySelect = (day) => {
    if (!day) return;
    setSelectedDay(day);
    applyHierarchyFilter(selectedFY, selectedQuarter, selectedMonth, day);
    setOpen(false);
  };

  const handleCustomFrom = (day) => {
    if (!day) return;
    setCustomFrom(day);
    if (customTo && day > customTo) {
      setCustomTo(null);
      setPickingWhich('to');
    } else if (customTo) {
      onDateChange(formatDateStr(day), formatDateStr(customTo));
      setOpen(false);
    } else {
      setPickingWhich('to');
    }
  };

  const handleCustomTo = (day) => {
    if (!day) return;
    setCustomTo(day);
    if (customFrom) {
      onDateChange(formatDateStr(customFrom), formatDateStr(day));
      setOpen(false);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedFY(null);
    setSelectedQuarter(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    setCustomFrom(null);
    setCustomTo(null);
    setPickingWhich('from');
    onDateChange(null, null);
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setSelectedFY(null);
    setSelectedQuarter(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    setCustomFrom(null);
    setCustomTo(null);
    setPickingWhich('from');
  };

  const fiscalYears = getFiscalYearRange();

  // Breadcrumb
  const breadcrumb = [];
  if (selectedFY !== null) breadcrumb.push({ label: fyLabel(selectedFY), action: () => { setSelectedQuarter(null); setSelectedMonth(null); setSelectedDay(null); applyHierarchyFilter(selectedFY, null, null, null); } });
  if (selectedQuarter) { const q = FY_QUARTERS.find(q => q.value === selectedQuarter); breadcrumb.push({ label: q.label, action: () => { setSelectedMonth(null); setSelectedDay(null); applyHierarchyFilter(selectedFY, selectedQuarter, null, null); } }); }
  if (selectedMonth !== null) breadcrumb.push({ label: MONTHS[selectedMonth].slice(0, 3), action: () => { setSelectedDay(null); applyHierarchyFilter(selectedFY, selectedQuarter, selectedMonth, null); } });

  // Get months to show based on quarter
  const visibleMonths = selectedQuarter
    ? FY_QUARTERS.find(q => q.value === selectedQuarter).months
    : [3, 4, 5, 6, 7, 8, 9, 10, 11, 0, 1, 2]; // Apr-Mar order

  // Calendar year for selected month
  const calendarYear = selectedMonth !== null && selectedFY !== null
    ? (selectedMonth >= 3 ? selectedFY : selectedFY + 1)
    : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'gap-2 h-10 text-sm font-normal',
            activeLabel ? 'text-foreground' : 'text-muted-foreground'
          )}
          data-testid="date-hierarchy-filter"
        >
          <CalendarDays className="w-4 h-4" />
          {activeLabel || 'Date Filter'}
          {activeLabel && (
            <span
              role="button"
              onClick={handleClear}
              className="ml-1 hover:bg-muted rounded-full p-0.5"
              data-testid="date-filter-clear"
            >
              <X className="w-3 h-3" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        {/* Mode tabs */}
        <div className="flex border-b">
          <button
            onClick={() => switchMode('hierarchy')}
            className={cn(
              'flex-1 px-4 py-2.5 text-sm font-medium transition-colors',
              mode === 'hierarchy'
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            )}
            data-testid="date-mode-hierarchy"
          >
            <CalendarDays className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
            Drill Down
          </button>
          <button
            onClick={() => switchMode('custom')}
            className={cn(
              'flex-1 px-4 py-2.5 text-sm font-medium transition-colors',
              mode === 'custom'
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            )}
            data-testid="date-mode-custom"
          >
            <CalendarRange className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
            Custom Range
          </button>
        </div>

        {mode === 'hierarchy' && (
          <div className="p-3 min-w-[280px]">
            {/* Breadcrumb */}
            {breadcrumb.length > 0 && (
              <div className="flex items-center gap-1 mb-3 text-xs">
                <button
                  onClick={() => { setSelectedFY(null); setSelectedQuarter(null); setSelectedMonth(null); setSelectedDay(null); onDateChange(null, null); }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  All
                </button>
                {breadcrumb.map((b, i) => (
                  <React.Fragment key={i}>
                    <ChevronRight className="w-3 h-3 text-muted-foreground" />
                    <button onClick={b.action} className="text-primary font-medium hover:underline">
                      {b.label}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* Level: Fiscal Year */}
            {selectedFY === null && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Select Fiscal Year</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {fiscalYears.map(fy => (
                    <Button
                      key={fy}
                      variant="outline"
                      size="sm"
                      onClick={() => handleFYSelect(fy)}
                      className="text-sm flex flex-col h-auto py-2"
                      data-testid={`fy-${fy}`}
                    >
                      <span className="font-semibold">{fyLabel(fy)}</span>
                      <span className="text-[10px] text-muted-foreground">Apr {fy} - Mar {fy + 1}</span>
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Level: Quarter */}
            {selectedFY !== null && !selectedQuarter && selectedMonth === null && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Select Quarter</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {FY_QUARTERS.map(q => (
                    <Button
                      key={q.value}
                      variant="outline"
                      size="sm"
                      onClick={() => handleQuarterSelect(q.value)}
                      className="text-sm flex flex-col h-auto py-2"
                      data-testid={`quarter-${q.value}`}
                    >
                      <span className="font-semibold">{q.label}</span>
                      <span className="text-[10px] text-muted-foreground">{q.display}</span>
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Level: Month */}
            {selectedFY !== null && selectedQuarter && selectedMonth === null && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Select Month</p>
                <div className="grid grid-cols-3 gap-1.5">
                  {visibleMonths.map(m => (
                    <Button
                      key={m}
                      variant="outline"
                      size="sm"
                      onClick={() => handleMonthSelect(m)}
                      className="text-sm"
                      data-testid={`month-${m}`}
                    >
                      {MONTHS[m].slice(0, 3)}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Level: Day */}
            {selectedFY !== null && selectedMonth !== null && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Select Day</p>
                <Calendar
                  mode="single"
                  selected={selectedDay}
                  onSelect={handleDaySelect}
                  defaultMonth={new Date(calendarYear, selectedMonth)}
                  month={new Date(calendarYear, selectedMonth)}
                  className="p-0"
                />
              </div>
            )}
          </div>
        )}

        {mode === 'custom' && (
          <div className="p-3 min-w-[280px]">
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => setPickingWhich('from')}
                className={cn(
                  'flex-1 text-left px-3 py-2 rounded-md border text-sm',
                  pickingWhich === 'from' ? 'border-primary ring-1 ring-primary' : 'border-border'
                )}
                data-testid="custom-from-btn"
              >
                <span className="text-[10px] text-muted-foreground block">Start Date</span>
                <span className={customFrom ? 'text-foreground' : 'text-muted-foreground'}>
                  {customFrom ? formatDisplay(customFrom) : 'Pick date'}
                </span>
              </button>
              <button
                onClick={() => setPickingWhich('to')}
                className={cn(
                  'flex-1 text-left px-3 py-2 rounded-md border text-sm',
                  pickingWhich === 'to' ? 'border-primary ring-1 ring-primary' : 'border-border'
                )}
                data-testid="custom-to-btn"
              >
                <span className="text-[10px] text-muted-foreground block">End Date</span>
                <span className={customTo ? 'text-foreground' : 'text-muted-foreground'}>
                  {customTo ? formatDisplay(customTo) : 'Pick date'}
                </span>
              </button>
            </div>
            <Calendar
              mode="single"
              selected={pickingWhich === 'from' ? customFrom : customTo}
              onSelect={pickingWhich === 'from' ? handleCustomFrom : handleCustomTo}
              disabled={pickingWhich === 'to' && customFrom ? { before: customFrom } : undefined}
              className="p-0"
            />
            {customFrom && customTo && (
              <Button
                size="sm"
                className="w-full mt-2"
                onClick={() => {
                  onDateChange(formatDateStr(customFrom), formatDateStr(customTo));
                  setOpen(false);
                }}
                data-testid="custom-apply-btn"
              >
                Apply Range
              </Button>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
